/**
 * Real Time Analytics → Live Operations.
 *
 * The real-data supervisor console. Same panels as the Campaign Summary demo
 * dashboard, with the demo overlay removed entirely rather than switched off:
 * this file does not import `demo-mode.js`, `useDemoOverlay` or anything under
 * `campaign-summary/demo/`, so no flag can make it render simulated numbers.
 * If the tenant has no traffic, this page says so.
 *
 * DATA — both feeds are real:
 *   live     `supervisor:*` sockets → `useSupervisorStore`, pushed by the
 *            ws-gateway broadcaster every 3s plus an immediate update on every
 *            agent status transition. Built from the Redis presence hashes and
 *            the live call sessions (`buildSupervisorFeed`).
 *   history  `GET /supervisor/campaign-summary`, polled — aggregates `calls`.
 *
 * ACTIONS — every control reaches a server:
 *   call controls   `POST /supervisor/{action}/:callId`, the full set the API
 *                   registers (listen / whisper / coach / barge / hold /
 *                   unhold / park / unpark / conference).
 *   agent controls  `supervisor:force_pause` / `supervisor:force_logout` over
 *                   the gateway socket.
 * Both are asynchronous: success arrives as a state change on the live feed,
 * failure as a `supervisor:alert` forwarded from the worker by ws-gateway's
 * supervisor-bridge. Nothing here optimistically mutates a row, because a
 * transition the FSM rejects would leave the UI showing a lie.
 *
 * MONITORED AUDIO ARRIVES ON THE SUPERVISOR'S SOFTPHONE, not in this tab —
 * listen/whisper/coach/barge originate a leg to the supervisor's own extension
 * and bridge it to an ARI snoop of the call. A supervisor with no extension
 * gets an explanatory alert instead of silence.
 *
 * LAYOUT — react-grid-layout, persisted per user on `users.supervisor_layout`
 * via `GET/PUT /agent/me/supervisor-layout`. Shared with the demo dashboard on
 * purpose: it is the same person arranging the same panels, and giving this page
 * its own key would silently discard a layout they had already set up.
 */
import { useEffect, useMemo, useRef, useState, type JSX } from 'react'
import { Lock, LockOpen, RefreshCw, RotateCcw } from 'lucide-react'
import { ProgressSpinner } from 'primereact/progressspinner'
import { Message } from 'primereact/message'

import { Responsive, WidthProvider } from 'react-grid-layout'
import type { Layout } from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
// Scoped under `.kc-cs` — the shared visual system for both supervisor
// dashboards, including the 3px radius ceiling. Imported last so it wins over
// both Lara and react-grid-layout.
import './campaign-summary/dashboard.css'

import { useSupervisorStore } from '../../store/supervisorStore.js'
import { useAuthStore } from '../../store/authStore.js'
import { useUiStore } from '../../store/uiStore.js'
import { api } from '../../api/client.js'

import { buildPivot } from './campaign-summary/format.js'
import { useLiveSupervisorActions } from './campaign-summary/live-actions.js'
import {
  useCampaignHistory,
  useCampaignOptions,
  useSupervisorFeed,
  useSupervisorLiveBridge,
  INTERVAL_OPTIONS,
  WINDOW_OPTIONS,
} from './campaign-summary/useCampaignSummary.js'
import Select from '../../components/ui/Select.js'
import {
  AgentPerformancePanel,
  CampaignPerformancePanel,
  DispositionPanel,
  IntervalPivotPanel,
  QueuePerformancePanel,
  ALL_CALL_CONTROLS,
} from './campaign-summary/panels.js'
// Store-connected wrappers for the per-second panels. They subscribe BELOW the
// grid, which keeps the live tick out of this component's render path.
import {
  LiveAgentsPanel,
  LiveFeedBadge,
  LiveCallsPanel,
  LiveKpiPanel,
  LiveQueueActivityPanel,
  LiveStateCountPanel,
} from './campaign-summary/live-panels.js'

const ReactGridLayout = WidthProvider(Responsive)

/**
 * Live operational panels sit above the fold; the historical tables stack
 * below, because the first question this page is opened to answer is "what is
 * happening now", not "what happened at 09:30".
 */
const DEFAULT_LAYOUT: Layout[] = [
  { i: 'kpi', x: 0, y: 0, w: 12, h: 3, minW: 4, minH: 2 },
  { i: 'agents', x: 0, y: 3, w: 6, h: 8, minW: 3, minH: 3 },
  { i: 'calls', x: 6, y: 3, w: 4, h: 8, minW: 3, minH: 3 },
  { i: 'states', x: 10, y: 3, w: 2, h: 8, minW: 2, minH: 3 },
  { i: 'liveQueues', x: 0, y: 11, w: 12, h: 5, minW: 4, minH: 3 },
  { i: 'pivot', x: 0, y: 16, w: 12, h: 11, minW: 6, minH: 5 },
  { i: 'agentPerf', x: 0, y: 27, w: 8, h: 8, minW: 4, minH: 3 },
  { i: 'dispositions', x: 8, y: 27, w: 4, h: 8, minW: 2, minH: 3 },
  { i: 'queuePerf', x: 0, y: 35, w: 6, h: 7, minW: 3, minH: 3 },
  { i: 'campaignPerf', x: 6, y: 35, w: 6, h: 7, minW: 3, minH: 3 },
]

const DEFAULT_LAYOUT_MOBILE: Layout[] = [
  { i: 'kpi', x: 0, y: 0, w: 1, h: 6 },
  { i: 'agents', x: 0, y: 6, w: 1, h: 8 },
  { i: 'calls', x: 0, y: 14, w: 1, h: 7 },
  { i: 'states', x: 0, y: 21, w: 1, h: 6 },
  { i: 'liveQueues', x: 0, y: 27, w: 1, h: 6 },
  { i: 'pivot', x: 0, y: 33, w: 1, h: 10 },
  { i: 'agentPerf', x: 0, y: 43, w: 1, h: 8 },
  { i: 'dispositions', x: 0, y: 51, w: 1, h: 7 },
  { i: 'queuePerf', x: 0, y: 58, w: 1, h: 7 },
  { i: 'campaignPerf', x: 0, y: 65, w: 1, h: 7 },
]

const LAYOUT_SAVE_DEBOUNCE_MS = 750

/** Toolbar buttons share the control chrome (3px ring + shadow) at the
 *  compact `sm` height so they sit flush with the <Select> triggers. */
const TB_BUTTON =
  'flex h-8 items-center justify-center gap-1.5 rounded-[3px] bg-white px-2.5 text-[12px] font-medium text-slate-600 ring-1 ring-slate-900/[0.08] shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition-all duration-150 hover:text-slate-800 hover:ring-slate-900/[0.16] hover:shadow-[0_2px_8px_-2px_rgba(15,23,42,0.12)] disabled:cursor-not-allowed disabled:opacity-40'
const TB_ICON_BUTTON = `${TB_BUTTON} w-8 px-0`

export default function LiveOperationsPage(): JSX.Element {
  const alerts = useSupervisorStore((s) => s.alerts)
  const clearAlert = useSupervisorStore((s) => s.clearAlert)

  const addNotification = useUiStore((s) => s.addNotification)
  const permissions = useAuthStore((s) => s.user?.permissions ?? [])
  const csrfToken = useAuthStore((s) => s.csrfToken)

  const canView =
    permissions.includes('supervisor:campaign_summary') || permissions.includes('*')
  const canForceLogout =
    permissions.includes('agents:force_logout') || permissions.includes('*')

  const [campaignId, setCampaignId] = useState<string>('')
  const [intervalMin, setIntervalMin] = useState<string>('30')
  const [hours, setHours] = useState<number>(12)
  const [locked, setLocked] = useState(true)

  const [layouts, setLayouts] = useState<Record<string, Layout[]>>({
    lg: DEFAULT_LAYOUT,
    sm: DEFAULT_LAYOUT_MOBILE,
  })
  const [layoutLoading, setLayoutLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Live socket feed only. There is deliberately NO page-level tick: a 1s tick
  // here would re-render `WidthProvider` and all ten grid items once a second,
  // forever. Per-second updates travel through `campaign-summary/live-store.ts`
  // straight to the live panels, which subscribe BELOW the grid.
  useSupervisorFeed()
  // Bridge the socket store into `live-store` — the ONLY writer the live
  // panels read. The demo page got this free inside useDemoOverlay; with no
  // overlay mounted, skipping this hook leaves every live panel empty while
  // the feed runs unseen into useSupervisorStore.
  useSupervisorLiveBridge()

  const campaignOptions = useCampaignOptions()
  const history = useCampaignHistory({
    intervalMin,
    hours,
    campaignId: campaignId || null,
  })

  const actions = useLiveSupervisorActions()

  /* ── Saved layout ─────────────────────────────────────────────────── */

  useEffect(() => {
    let cancelled = false
    api
      .get<{ layout: Record<string, unknown> }>('/agent/me/supervisor-layout')
      .then(({ layout }) => {
        if (cancelled) return
        const stored = layout ?? {}
        const lg =
          Array.isArray(stored['lg']) && stored['lg'].length > 0
            ? (stored['lg'] as Layout[])
            : DEFAULT_LAYOUT
        const sm =
          Array.isArray(stored['sm']) && stored['sm'].length > 0
            ? (stored['sm'] as Layout[])
            : DEFAULT_LAYOUT_MOBILE
        setLayouts({ lg, sm })
      })
      .catch(() => addNotification('Could not load saved layout', 'error'))
      .finally(() => {
        if (!cancelled) setLayoutLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [addNotification])

  // Mirror of `layouts` that is safe to read from an unmount cleanup. Reading
  // the state variable there would close over its value from the render that
  // registered the effect, which is exactly the stale layout we must not save.
  const latestLayouts = useRef(layouts)
  latestLayouts.current = layouts

  /** Whether an edit is waiting to be written. Cleared once the PUT is issued. */
  const pendingSave = useRef(false)

  function persistLayout(next: Record<string, Layout[]>): void {
    pendingSave.current = false
    setSaving(true)
    api
      .put('/agent/me/supervisor-layout', { layout: next })
      .catch(() => addNotification('Failed to save layout', 'error'))
      .finally(() => setSaving(false))
  }

  // FLUSH on unmount rather than discard. Dragging a panel and immediately
  // navigating away otherwise loses the move: the debounce timer is cleared
  // before it fires and the change never reaches the server. `keepalive` lets
  // the request outlive the page teardown, which a normal fetch would not.
  useEffect(() => {
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current)
      if (!pendingSave.current) return
      pendingSave.current = false
      void fetch('/api/agent/me/supervisor-layout', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken ? { 'x-csrf-token': csrfToken } : {}),
        },
        credentials: 'include',
        keepalive: true,
        body: JSON.stringify({ layout: latestLayouts.current }),
      }).catch(() => undefined)
    }
  }, [csrfToken])

  function handleLayoutChange(
    _current: Layout[],
    allLayouts: Record<string, Layout[]>,
  ): void {
    setLayouts(allLayouts)
    // Only persist when the grid is unlocked. react-grid-layout fires
    // onLayoutChange on mount and on every breakpoint/width change too, so
    // saving unconditionally would overwrite the user's stored desktop layout
    // with a responsive reflow they never asked for.
    if (locked) return
    pendingSave.current = true
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(
      () => persistLayout(allLayouts),
      LAYOUT_SAVE_DEBOUNCE_MS,
    )
  }

  /**
   * Commit immediately when a drag or resize ENDS. `onLayoutChange` fires
   * continuously during the gesture, so every save would be debounced behind
   * the next frame of movement, and any interruption before the timer elapsed
   * would drop the edit.
   */
  function handleGestureEnd(_layout: Layout[]): void {
    if (locked) return
    if (saveTimer.current) clearTimeout(saveTimer.current)
    persistLayout(latestLayouts.current)
  }

  /** Locking ends an editing session — flush anything still outstanding. */
  function toggleLock(): void {
    if (!locked) {
      if (saveTimer.current) clearTimeout(saveTimer.current)
      if (pendingSave.current) persistLayout(latestLayouts.current)
    }
    setLocked((v) => !v)
  }

  function resetLayout(): void {
    const next = { lg: DEFAULT_LAYOUT, sm: DEFAULT_LAYOUT_MOBILE }
    setLayouts(next)
    persistLayout(next)
    addNotification('Layout reset to default', 'info')
  }

  /* ── Derived ──────────────────────────────────────────────────────── */

  const data = history.data

  // Queue id → display name, sourced from the history endpoint so the LIVE
  // panels can show real queue names instead of raw uuids.
  const queueNames = useMemo(() => {
    const map = new Map<string, string>()
    for (const q of data.queues) map.set(q.queueId, q.queueName)
    return map
  }, [data.queues])

  const pivot = useMemo(() => buildPivot(data.intervals), [data.intervals])

  if (!canView) {
    return (
      <div className="p-8">
        <Message
          severity="error"
          text="You do not have permission to view the Campaign Summary."
        />
      </div>
    )
  }

  if (layoutLoading) {
    return (
      <div className="p-8 flex justify-center" data-testid="live-operations-loading">
        <ProgressSpinner style={{ width: 36, height: 36 }} strokeWidth="4" />
      </div>
    )
  }

  return (
    <div
      className={`kc-cs flex flex-col h-full min-h-0 bg-slate-50 ${
        locked ? 'kc-locked' : ''
      }`}
    >
      {/* ── Toolbar ──────────────────────────────────────────────────── */}
      <div className="shrink-0 border-b border-slate-200 bg-white px-3 py-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-baseline gap-2 mr-auto min-w-0">
            {/* Matches the sidebar label. The simulated dashboard is the one
                that carries a qualifier ("Demo"), not this one — the real
                numbers should read as the default. */}
            <h1 className="text-[15px] font-semibold text-slate-800 truncate">
              Campaign Summary
            </h1>
            <LiveFeedBadge />
            {history.loading && (
              <ProgressSpinner style={{ width: 14, height: 14 }} strokeWidth="6" />
            )}
          </div>

          <Select
            ariaLabel="Campaign"
            size="sm"
            value={campaignId}
            options={campaignOptions}
            onChange={setCampaignId}
            className="w-48"
            filterable={campaignOptions.length > 8}
          />
          <Select
            ariaLabel="Interval"
            size="sm"
            value={intervalMin}
            options={INTERVAL_OPTIONS}
            onChange={setIntervalMin}
            className="w-36"
          />
          <Select
            ariaLabel="Time window"
            size="sm"
            value={String(hours)}
            options={WINDOW_OPTIONS.map((o) => ({ label: o.label, value: String(o.value) }))}
            onChange={(v) => setHours(Number(v))}
            className="w-40"
          />

          <button
            type="button"
            aria-label="Refresh now"
            title="Refresh now"
            onClick={history.refresh}
            className={TB_ICON_BUTTON}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${history.loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            title={locked ? 'Unlock to drag and resize panels' : 'Lock the layout — changes are saved automatically'}
            onClick={toggleLock}
            className={`${TB_BUTTON} ${locked ? '' : 'text-amber-700 ring-amber-300/60 hover:text-amber-800 hover:ring-amber-400/60'}`}
          >
            {locked ? <Lock className="h-3.5 w-3.5" /> : <LockOpen className="h-3.5 w-3.5" />}
            {locked ? 'Locked' : 'Editing'}
          </button>
          {!locked && (
            <button
              type="button"
              aria-label="Reset layout"
              title="Reset to the default layout"
              onClick={resetLayout}
              className={TB_ICON_BUTTON}
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
          )}
          {saving && <ProgressSpinner style={{ width: 16, height: 16 }} strokeWidth="6" />}
        </div>

        {history.error && (
          <div className="mt-2 rounded-[3px] border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-800">
            Historical metrics unavailable ({history.error}). Live agent and call
            data below is unaffected.
          </div>
        )}
      </div>

      {/* Supervisor alerts, including action failures forwarded from the worker
          — this is where "listen could not run, and why" surfaces. */}
      {alerts.length > 0 && (
        <div className="shrink-0 flex flex-col gap-1 px-3 pt-2">
          {alerts.map((a) => (
            <Message
              key={a.id}
              severity={a.severity === 'error' ? 'error' : a.severity}
              text={a.message}
              onClick={() => clearAlert(a.id)}
              pt={{
                root: {
                  className:
                    'rounded-[3px] justify-start text-[12px] cursor-pointer',
                },
              }}
            />
          ))}
        </div>
      )}

      {/* ── Grid ─────────────────────────────────────────────────────── */}
      <div className="flex-1 min-h-0 overflow-auto p-2">
        <ReactGridLayout
          className="layout"
          layouts={layouts}
          breakpoints={{ lg: 1024, sm: 0 }}
          cols={{ lg: 12, sm: 1 }}
          rowHeight={44}
          margin={[8, 8]}
          containerPadding={[0, 0]}
          onLayoutChange={handleLayoutChange}
          onDragStop={handleGestureEnd}
          onResizeStop={handleGestureEnd}
          isDraggable={!locked}
          isResizable={!locked}
          draggableHandle=".p-panel-header"
        >
          <div key="kpi">
            <LiveKpiPanel totals={data.totals} />
          </div>
          <div key="agents">
            <LiveAgentsPanel actions={actions} canForceLogout={canForceLogout} />
          </div>
          <div key="calls">
            {/* No `onListen` override — Listen goes to the real supervisor
                endpoint and the audio arrives on the supervisor's softphone.
                The demo dashboard's in-browser MP3 player is deliberately
                absent. */}
            <LiveCallsPanel
              queueNames={queueNames}
              actions={actions}
              controls={ALL_CALL_CONTROLS}
            />
          </div>
          <div key="states">
            <LiveStateCountPanel />
          </div>
          <div key="liveQueues">
            <LiveQueueActivityPanel queueNames={queueNames} />
          </div>
          <div key="pivot">
            <IntervalPivotPanel
              intervals={data.intervals}
              pivot={pivot}
              totals={data.totals}
            />
          </div>
          <div key="agentPerf">
            <AgentPerformancePanel rows={data.agents} />
          </div>
          <div key="dispositions">
            <DispositionPanel rows={data.dispositions} />
          </div>
          <div key="queuePerf">
            <QueuePerformancePanel rows={data.queues} />
          </div>
          <div key="campaignPerf">
            <CampaignPerformancePanel rows={data.campaigns} />
          </div>
        </ReactGridLayout>
      </div>
    </div>
  )
}
